/**
 * Hash-based SPA router with role-based route guards.
 * Routes and page titles come from the role registry.
 */

import { auth } from './auth';
import { registry } from './roles/registry';
import { $currentPage, $isLoading } from './stores/ui';
import type { Route } from './types/registry';

interface PageModule {
    render(container: HTMLElement): Promise<void> | void;
    cleanup?(): void;
}

let _currentModule: PageModule | null = null;

function checkAccess(route: Route): boolean {
    if (!route.role) return true;
    if (route.role === 'admin') return auth.isAdmin();
    if (route.role === 'dept-head') return auth.isDeptHead();
    return false;
}

async function navigate(hash: string): Promise<void> {
    const path = hash.replace('#', '') || '/';
    const routes = registry.getRoutes();
    const pageTitles = registry.getPageTitles();
    const route = routes[path];
    const content = document.getElementById('content') as HTMLElement;
    const titleEl = document.getElementById('page-title');

    if (titleEl) titleEl.textContent = pageTitles[path] || 'NIR PBX';
    document.title = (pageTitles[path] || 'NIR PBX') + ' — sip.nir.gov.ua';

    if (!route) {
        content.innerHTML = '<div class="loading">404 — Сторінку не знайдено</div>';
        return;
    }

    // Route guard: check role before loading page
    if (!checkAccess(route)) {
        content.innerHTML = `
            <div class="empty-state" style="padding:80px 20px">
                <div style="font-size:48px;margin-bottom:16px">&#128274;</div>
                <h3 style="margin-bottom:8px;font-size:20px">Доступ заборонено</h3>
                <p style="color:var(--color-text-secondary)">У вас немає прав для перегляду цієї сторінки.</p>
                <a href="#/" class="btn btn-outline" style="margin-top:20px">На головну</a>
            </div>`;
        return;
    }

    $currentPage.set(path);
    $isLoading.set(true);
    content.innerHTML = '<div class="loading">Завантаження...</div>';
    try {
        // Clean up previous page module if it exports cleanup()
        if (_currentModule && typeof _currentModule.cleanup === 'function') {
            _currentModule.cleanup();
        }
        const module: PageModule = await route.loader();
        _currentModule = module;
        content.innerHTML = '';
        await module.render(content);
    } catch (e: any) {
        content.innerHTML = `<div class="loading">Помилка: ${(e instanceof Error ? e.message : String(e))}</div>`;
        console.error(e);
        import('./sentry').then((s) => s.captureException(e)).catch(() => {});
    } finally {
        $isLoading.set(false);
    }
}

export const router = {
    init(): void {
        window.addEventListener('hashchange', () => navigate(location.hash));
        navigate(location.hash || '#/');
    },
};
